import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { TrackedButtonClickName } from '~/services/gtm.client';

import Tracked from '../../Tracked';

type Props = {
  isExpanded: boolean;
  onSizeSelect: (size: string) => void;
  selectedSize?: string;
  sizes: string[];
};

export default function SizeSelectorGrid({
  onSizeSelect,
  selectedSize,
  sizes,
  isExpanded
}: Props) {
  const { t } = useTranslation('pdp');

  return (
    <div
      className={`flex flex-row gap-2 ${isExpanded ? 'flex-wrap' : 'flex-nowrap overflow-scroll pb-5'}`}
    >
      <SizeButton
        isGridExpanded={isExpanded}
        name="ALL_SIZES"
        isSelected={selectedSize === ''}
        onClick={() => onSizeSelect('')}
      >
        {t('all')}
      </SizeButton>
      {sizes.map((size) => (
        <SizeButton
          isGridExpanded={isExpanded}
          key={size}
          name={`SIZE_${size}`}
          isSelected={selectedSize === size}
          onClick={() => onSizeSelect(size)}
        >
          {size}
        </SizeButton>
      ))}
    </div>
  );
}

function SizeButton({
  children,
  isSelected,
  isGridExpanded,
  name,
  onClick
}: {
  children: string;
  isGridExpanded: boolean;
  isSelected: boolean;
  name: TrackedButtonClickName;
  onClick: () => void;
}) {
  const baseClassNames =
    'w-full h-full text-sm rounded-3xl border border-grey-divide p-2';

  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (isSelected && !isGridExpanded) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    }
  }, [isSelected, isGridExpanded]);

  return (
    <span className="min-w-[60px] max-w-[72px] grow basis-0 " ref={ref}>
      <Tracked.Button
        name={name}
        variant="none"
        pagePosition="Size Selection"
        className={`${baseClassNames} ${isSelected ? 'bg-black text-white' : 'text-black'}`}
        onClick={onClick}
      >
        {children}
      </Tracked.Button>
    </span>
  );
}
